import type { Rarity } from '@crackpoint/shared/types';
import { formatTime, formatScore } from '@crackpoint/shared/utils';
import { getBranding } from '@crackpoint/shared/config';

// Re-export formatting functions from shared package
export { formatTime, formatScore };

// API configuration
export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// Product identity (driven by build-time adapter mode)
const branding = getBranding();
export const PRODUCT_NAME = branding.productName;
export const PRODUCT_FULL_NAME = branding.productFullName;

// Game configuration defaults
export const DEFAULT_CODE_LENGTH = 3;
export const MAX_CODE_LENGTH = 6;
export const MIN_CODE_LENGTH = 3;

// Hint pool configuration
export const DEFAULT_HINT_POOL_SIZE = 20;
export const MAX_HINTS_DRAWN = 10;

// Scoring configuration
export const SCORE_PER_HINT = 10;
export const SCORE_PER_GUESS = 1;
export const SCORE_SOLVE_BONUS = 100;
export const SCORE_ROOM_BONUS = 50;

// Timer configuration
export const TIMER_UPDATE_INTERVAL = 100; // milliseconds
export const TIMER_PRECISION = 2; // decimal places

// Leaderboard configuration
export const LEADERBOARD_PAGE_SIZE = 20;
export const LEADERBOARD_REFRESH_INTERVAL = 30000; // milliseconds

// Animation durations
export const ANIMATION_DURATION = 300;
export const HINT_DRAW_ANIMATION = 500;
export const GUESS_FEEDBACK_ANIMATION = 400;
export const ROOM_TRANSITION_ANIMATION = 800;

// Local storage keys
export const STORAGE_KEYS = {
  SESSION_ID: 'crackpoint_session_id',
  SETTINGS: 'crackpoint_settings',
  HIGH_SCORES: 'crackpoint_high_scores',
  GAME_STATE: 'crackpoint_game_state',
} as const;

// Rarity colours for UI
export const RARITY_COLOURS: Record<Rarity, { primary: string; glow: string; text: string }> = {
  common: {
    primary: '#6b7280',
    glow: 'rgba(107, 114, 128, 0.5)',
    text: '#9ca3af',
  },
  uncommon: {
    primary: '#22c55e',
    glow: 'rgba(34, 197, 94, 0.5)',
    text: '#4ade80',
  },
  rare: {
    primary: '#3b82f6',
    glow: 'rgba(59, 130, 246, 0.5)',
    text: '#60a5fa',
  },
  epic: {
    primary: '#a855f7',
    glow: 'rgba(168, 85, 247, 0.5)',
    text: '#c084fc',
  },
  legendary: {
    primary: '#fbbf24',
    glow: 'rgba(251, 191, 36, 0.5)',
    text: '#fcd34d',
  },
};

// Power-up colours
export const POWER_UP_COLOURS = {
  boost: '#f59e0b',
  surge_i: '#ef4444',
  surge_ii: '#ec4899',
  oracle: '#8b5cf6',
} as const;

// Difficulty scaling
export const DIFFICULTY_SCALING = {
  CODE_LENGTH_INCREASE_AT: [5, 10, 15, 20, 25],
  HINT_POOL_INCREASE: 5,
} as const;

// Input limits
export const INPUT_DEBOUNCE_MS = 150;
export const GUESS_SUBMIT_COOLDOWN = 500;

// Digit display
export const DIGIT_SYMBOLS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'] as const;

// Room progression messages
export const ROOM_MESSAGES = {
  START: ['SYSTEM INITIALISED...', 'CODE LOCK ENGAGED...', 'SECURITY PROTOCOL ACTIVE...'],
  SOLVE: ['ACCESS GRANTED', 'LOCK BYPASSED', 'CODE ACCEPTED'],
  GAME_OVER: ['CONNECTION LOST', 'SESSION TERMINATED', 'ACCESS DENIED'],
} as const;
